import assertCustomGuardValue from 'common/bento/lib/assertCustomGuardValue';
import assertEventType from 'common/bento/lib/assertEventType';
import { MachineGuards } from 'common/bento/types/MachineConfig';
// eslint-disable-next-line no-restricted-imports -- Feature flags to be migrated
import getIsEi from 'features/Onboarding/lib/getIsEi';
import { ApplicationType } from 'features/Onboarding/types';

import { getHasAlreadyFilledProfile, isSupposedDirector } from '../libs';
import { PersonalInfoErrorCode } from './errors';
import { Model } from './model';

export enum Guard {
  CanSeeResetPrompt = 'canSeeResetPrompt',
  HasProfileFilled = 'hasProfileFilled',
  HasSupposedDirectors = 'hasSupposedDirectors',
  IsApplicationType = 'isApplicationType',
  IsBeforeCompanyCreation = 'isBeforeCompanyCreation',
  IsDirectorMissingGenderError = 'isDirectorMissingGenderError',
  IsEi = 'isEi',
  IsRoleSufficient = 'isRoleSufficient',
}

const guards: MachineGuards<Model, Guard> = {
  canSeeResetPrompt: ({ getModuleData }) => {
    const { company } = getModuleData().viewer;

    if (!company) {
      return false;
    }

    return company.canResetOnboarding;
  },

  hasProfileFilled: ({ getModuleData }) => {
    const { profile } = getModuleData().viewer;

    return getHasAlreadyFilledProfile(profile);
  },

  hasSupposedDirectors: ({ getModuleData }) => {
    const { company } = getModuleData().viewer;

    if (!company) {
      return false;
    }

    return Boolean(company.supposedBeneficiaries?.some(isSupposedDirector));
  },

  isApplicationType: (context, event, { cond }) => {
    assertCustomGuardValue<ApplicationType>(cond);

    return cond.value === context.getModuleData().applicationType;
  },

  isBeforeCompanyCreation: ({ getModuleData }) =>
    !getModuleData().viewer.company,

  isDirectorMissingGenderError: (_, event) => {
    if (event.type !== 'error.platform.saveDirectorSelection') {
      throw new Error(
        `isDirectorMissingGenderError called on event ${event.type}`,
      );
    }

    const { data: error } = event;
    return error.message === PersonalInfoErrorCode.DIRECTOR_MISSING_GENDER;
  },

  isEi: ({ getModuleData }) => {
    const {
      applicationType,
      viewer: { company },
    } = getModuleData();

    return (
      applicationType === ApplicationType.ShineStart ||
      Boolean(company && getIsEi(company.profile))
    );
  },

  isRoleSufficient: ({ getModuleData }, event) => {
    assertEventType(event, 'SUBMIT_BUSINESS_ROLE');

    const { isDirector, isMajorShareholder } = event.data;

    if (getModuleData().applicationType === ApplicationType.CapitalDeposit) {
      return Boolean(isDirector);
    }

    return Boolean(isDirector || isMajorShareholder);
  },
};

export default guards;
